/*
 * Slot Grill: auto-add free grill gifts (v2, prepared Oct 13, 2026; replaces sg-free-balm.js v1).
 *   Grill Only (Full Size / Compact)       -> 1 free Meat Thermometer per grill
 *       discount "Complimentary Gift" (gid://shopify/DiscountAutomaticNode/1682349818085)
 *   Grill + 1/2 Griddles (Full Size / Compact) -> 1 free Seasoning Balm + Meat Thermometer Bundle per grill
 *       discount "Free Seasoning Balm with Grill + Griddle" (gid://shopify/DiscountAutomaticNode/1681305796837)
 * Rules
 * - Auto-added lines carry the hidden property _sg_gift = "thermo" | "bundle". Only those lines are ever
 *   changed or removed. Lines the shopper added themselves (paid) are never touched.
 * - Target auto qty = (qualifying grill units) - (shopper's own untagged units of that gift) - (units the
 *   shopper declined), floored at 0. Own units count toward the gift because the BXGY discount will make
 *   them free anyway; adding more would put full-price units in the cart.
 * - If the shopper removes/reduces an auto line while grill count did not go up, that is a decline: it is
 *   remembered for the session and not re-added. Declines reset when that grill type leaves the cart.
 * - Standalone Seasoning Balm is never added. Leftover v1 auto-balm lines (_sg_free_balm) are removed,
 *   because the balm is no longer discounted on its own.
 * - No loops: own cart events are ignored, one reconcile at a time, the plan is idempotent, and a failed
 *   add is not retried until the cart changes again.
 * Express checkout buttons (Shop Pay / Buy with Shop) skip the cart, so this cannot run for them.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const GRILL_ONLY = new Set([
  45710474150076, // Full Size - Grill Only
  46105345327292, // Compact - Grill Only
]);
const GRILL_GRIDDLE = new Set([
  45710474182844, // Full Size - Grill + 1 Griddle
  46105811910844, // Full Size - Grill + 2 Griddles
  46105345360060, // Compact - Grill + 1 Griddle
  46105360924860, // Compact - Grill + 2 Griddles
]);
const GIFTS = [
  { key: 'thermo', variant: 67689969582309, grills: GRILL_ONLY },    // Instant-Read Digital Meat Thermometer
  { key: 'bundle', variant: 67690068246757, grills: GRILL_GRIDDLE }, // Seasoning Balm + Meat Thermometer Bundle
];
const LEGACY_BALM = 46158666268860;
const LEGACY_PROP = '_sg_free_balm';
const PROP = '_sg_gift';
const SOURCE = 'sg-gifts';
const STATE_KEY = 'sgGiftsV2';

const sum = (lines) => lines.reduce((n, i) => n + i.quantity, 0);
const prop = (i, k) => (i.properties && i.properties[k]) || null;

/* Pure planner: (cart items, saved state) -> { ops, state }. No DOM / network, so it can be tested. */
export function plan(items, prev) {
  const state = {};
  const ops = [];
  for (const g of GIFTS) {
    const p = (prev && prev[g.key]) || { set: 0, grills: 0, declined: 0 };
    const grills = sum(items.filter((i) => g.grills.has(i.variant_id)));
    const lines = items.filter((i) => i.variant_id === g.variant);
    const auto = lines.filter((i) => prop(i, PROP) === g.key);
    const own = sum(lines.filter((i) => prop(i, PROP) !== g.key));
    const have = sum(auto);
    let declined = grills === 0 ? 0 : p.declined;
    // Shopper took auto units out (we last left `p.set`) and grill count did not rise: a decline.
    if (grills > 0 && have < p.set && grills <= p.grills) declined += p.set - have;
    const want = Math.max(0, grills - own);
    declined = Math.min(declined, want);
    const target = want - declined;
    if (auto.length === 0) {
      if (target > 0) ops.push({ op: 'add', id: g.variant, quantity: target, properties: { [PROP]: g.key } });
    } else {
      auto.forEach((line, n) => {
        const q = n === 0 ? target : 0; // merge duplicates into the first auto line
        if (line.quantity !== q) ops.push({ op: 'change', key: line.key, quantity: q });
      });
    }
    state[g.key] = { set: target, grills, declined };
  }
  for (const line of items) {
    if (line.variant_id === LEGACY_BALM && prop(line, LEGACY_PROP)) ops.push({ op: 'change', key: line.key, quantity: 0 });
  }
  return { ops, state };
}

/* Saved state with `set` replaced by the auto quantities really in the cart. */
export function observed(items, state) {
  const out = {};
  for (const g of GIFTS) {
    const have = sum(items.filter((i) => i.variant_id === g.variant && prop(i, PROP) === g.key));
    out[g.key] = { ...state[g.key], set: have };
  }
  return out;
}

const ss = {
  get: () => { try { return JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null'); } catch (_) { return null; } },
  set: (v) => { try { sessionStorage.setItem(STATE_KEY, JSON.stringify(v)); } catch (_) {} },
};

function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

async function post(url, body) {
  const ids = sectionIds();
  if (ids.length) { body.sections = ids.join(','); body.sections_url = location.pathname; }
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(url + ' failed ' + res.status);
  return res.json();
}

async function getCart() {
  const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('cart.js failed');
  return res.json();
}

function announce(cart, sections) {
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, {
    source: SOURCE,
    itemCount: cart.item_count,
    sections: sections || undefined,
  }));
}

let running = false;
let again = false;

async function reconcile() {
  if (running) { again = true; return; }
  running = true;
  try {
    const cart = await getCart();
    const { ops, state } = plan(cart.items || [], ss.get());
    let sections = null;
    let changed = false;
    for (const o of ops) {
      try {
        const r = o.op === 'add'
          ? await post('/cart/add.js', { items: [{ id: o.id, quantity: o.quantity, properties: o.properties }] })
          : await post('/cart/change.js', { id: o.key, quantity: o.quantity });
        sections = r.sections || sections;
        changed = true;
      } catch (e) {
        console.warn('[sg-gifts]', o, e); // e.g. gift out of stock or not published; not retried until next cart change
      }
    }
    if (ops.length) {
      // Record what actually landed (a failed add must not later look like a shopper decline).
      const fresh = await getCart();
      ss.set(observed(fresh.items || [], state));
      if (changed) announce(fresh, sections);
    } else {
      ss.set(state);
    }
  } catch (e) {
    console.warn('[sg-gifts]', e);
  } finally {
    running = false;
    if (again) { again = false; setTimeout(reconcile, 50); }
  }
}

let timer = null;
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = event && event.detail && event.detail.data;
  if (data && (data.source === SOURCE || data.didError)) return;
  clearTimeout(timer);
  timer = setTimeout(reconcile, 120);
});
window.addEventListener('pageshow', (e) => { if (e.persisted) reconcile(); });
reconcile();
